import React, { useEffect, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { heroContent } from '../data/mock';
import { fetchNews } from '../services/api';

const formatDate = (raw) => {
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const Hero = () => {
  const [headlines, setHeadlines] = useState([]);

  useEffect(() => {
    fetchNews('all', 5)
      .then((items) => setHeadlines(items || []))
      .catch(() => setHeadlines([]));
  }, []);

  return (
    <section className="relative overflow-hidden bg-[#e2ede6]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-10 lg:pt-16 pb-16 lg:pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-start">
          {/* Left content */}
          <div className="lg:col-span-8 relative">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
              <div className="relative z-10">
                <h1
                  className="font-light text-[#0b1a2e] leading-[0.95] tracking-tight"
                  style={{ fontFamily: 'Cormorant Garamond, serif' }}
                >
                  <span className="block text-[44px] md:text-[52px] lg:text-[58px]">
                    The World of
                  </span>
                  <span
                    className="block text-[72px] md:text-[92px] lg:text-[112px] font-medium mt-1"
                    style={{ letterSpacing: '-0.02em' }}
                  >
                    Emerald
                  </span>
                </h1>
                <p className="mt-8 text-[15px] leading-relaxed text-[#0b1a2e]/70 max-w-md">
                  {heroContent.description}
                </p>
                <Link
                  to="/verticals"
                  data-testid="hero-explore-verticals-btn"
                  className="mt-8 group inline-flex items-center gap-3 px-6 py-3.5 rounded-full border border-[#0b1a2e]/70 text-[#0b1a2e] hover:bg-[#0b1a2e] hover:text-white transition-all duration-300"
                >
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                  <span className="text-sm font-medium tracking-wide">
                    {heroContent.cta}
                  </span>
                </Link>
              </div>
              <div className="relative flex justify-center items-center">
                <div className="absolute inset-0 bg-gradient-to-tr from-emerald-200/40 to-transparent blur-3xl" />
                <img
                  src={heroContent.image}
                  alt="Emerald crystal"
                  className="relative w-full max-w-[420px] rounded-2xl object-cover aspect-square shadow-[0_20px_60px_-15px_rgba(15,92,61,0.35)] hero-float"
                />
              </div>
            </div>
          </div>

          {/* Right: Google News RSS Feed */}
          <div className="lg:col-span-4" data-testid="hero-market-news">
            <div className="flex items-baseline justify-between mb-4">
              <h3
                className="text-2xl text-[#0b1a2e]"
                style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}
              >
                Market Headlines
              </h3>
              <Link
                to="/news"
                className="text-xs font-semibold tracking-widest uppercase text-[#0f5c3d] hover:text-[#0b1a2e] transition"
              >
                All news →
              </Link>
            </div>

            <div className="bg-white rounded-2xl overflow-hidden shadow-[0_10px_40px_-12px_rgba(11,26,46,0.15)] border border-black/5">
              {headlines.length === 0 ? (
                <div className="p-8 text-center text-sm text-[#0b1a2e]/50">
                  Fetching headlines…
                </div>
              ) : (
                <>
                  {/* Featured (first item with image) */}
                  {headlines[0] && (
                    <a
                      href={headlines[0].link}
                      target="_blank"
                      rel="noreferrer"
                      className="block group"
                      data-testid="hero-featured-headline"
                    >
                      <div className="relative aspect-[16/9] overflow-hidden">
                        <img
                          src={headlines[0].image}
                          alt={headlines[0].title}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                          onError={(e) => {
                            e.currentTarget.src =
                              'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=800&q=80';
                          }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                        <div className="absolute bottom-3 left-4 right-4 text-white">
                          <div className="text-[10px] font-semibold uppercase tracking-widest opacity-90">
                            {formatDate(headlines[0].published)} · {headlines[0].source || 'News'}
                          </div>
                          <div className="mt-1 text-sm font-semibold leading-snug line-clamp-2">
                            {headlines[0].title}
                          </div>
                        </div>
                      </div>
                    </a>
                  )}

                  {/* Compact list */}
                  <ul className="divide-y divide-black/5">
                    {headlines.slice(1, 5).map((h) => (
                      <li key={h.id}>
                        <a
                          href={h.link}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-start gap-3 px-4 py-3 hover:bg-[#f7fbf8] transition"
                          data-testid="hero-headline-item"
                        >
                          <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#0f5c3d] shrink-0" />
                          <div className="min-w-0">
                            <div className="text-[11px] font-semibold uppercase tracking-widest text-[#0f5c3d]">
                              {formatDate(h.published)}{' '}
                              <span className="text-[#0b1a2e]/40 font-normal normal-case tracking-normal">
                                · {h.source || 'source'}
                              </span>
                            </div>
                            <div className="mt-0.5 text-[13px] font-medium text-[#0b1a2e] line-clamp-2">
                              {h.title}
                            </div>
                          </div>
                        </a>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
